import { useState, useCallback } from "react";
import type { UseLocalStorageArgs, UseLocalStorageResult } from "./types";

const readStorageValue = <T>(key: string, initialValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? (JSON.parse(item) as T) : initialValue;
  } catch {
    return initialValue;
  }
};

export const useLocalStorage = <T>({
  key,
  initialValue,
}: UseLocalStorageArgs<T>): UseLocalStorageResult<T> => {
  const [storedValue, setStoredValue] = useState<T>(() =>
    readStorageValue(key, initialValue),
  );

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const nextValue =
          value instanceof Function ? value(prev) : value;
        localStorage.setItem(key, JSON.stringify(nextValue));
        return nextValue;
      });
    },
    [key],
  );

  const removeValue = useCallback(() => {
    localStorage.removeItem(key);
    setStoredValue(initialValue);
  }, [key, initialValue]);

  return { value: storedValue, setValue, removeValue };
};
